import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Icone } from './Icone';
import type { NomeDoIcone } from './Icone';
import './estados.css';

/**
 * TELA 07 — "OS DIAS EM QUE ALGO DÁ ERRADO".
 *
 * 403, 404 e erro de carregamento acontecem DENTRO do shell: a sidebar e a topbar continuam de
 * pé, porque só aquela área falhou. Offline é faixa, não modal. Nenhum estado usa ilustração —
 * texto claro e uma ação.
 */

/** Estado vazio: ícone num quadrado cinza, título e uma frase. Nunca um zero no lugar. */
export function EstadoVazio({
  icone = 'clock',
  titulo,
  texto,
  children,
}: {
  icone?: NomeDoIcone;
  titulo: string;
  texto?: string;
  children?: ReactNode;
}) {
  return (
    <div className="es-vazio">
      <span className="es-vazio-icone" aria-hidden="true">
        <Icone nome={icone} tamanho={17} traco={1.5} />
      </span>
      <div className="es-vazio-titulo">{titulo}</div>
      {texto && <div className="es-vazio-texto">{texto}</div>}
      {children && <div className="es-acoes">{children}</div>}
    </div>
  );
}

/** "Não foi possível carregar X" — só a área falha; os indicadores ao redor continuam. */
export function ErroDeArea({ oQue, aoTentar }: { oQue: string; aoTentar: () => void }) {
  return (
    <div className="es-vazio es-erro-area" role="alert">
      <span className="es-vazio-icone es-icone-erro" aria-hidden="true">
        <Icone nome="warn" tamanho={17} traco={1.6} />
      </span>
      <div className="es-vazio-titulo">Não foi possível carregar {oQue}</div>
      <div className="es-vazio-texto">
        Só esta área falhou. O resto da tela continua atualizado.
      </div>
      <div className="es-acoes">
        <button type="button" className="es-botao" onClick={aoTentar}>
          <Icone nome="cycle" tamanho={15} traco={1.6} />
          Tentar de novo
        </button>
      </div>
    </div>
  );
}

/** 403 dentro do shell. Diz de quem é a área, sem dizer como contornar. */
export function SemAcesso({ area }: { area: string }) {
  return (
    <div className="es-pagina">
      <EstadoVazio
        icone="lock"
        titulo="Você não tem acesso a esta área"
        texto={`${area} é uma área de administração. Se acha que isso está incorreto, fale com um administrador da Rede Inova.`}
      >
        <button type="button" className="es-botao" onClick={() => window.history.back()}>
          Voltar
        </button>
        <a className="es-botao es-botao-primario" href="#/visao-geral">
          Ir para o início
        </a>
      </EstadoVazio>
    </div>
  );
}

/** 404 dentro do shell, com o endereço que não existe na topbar (feito pelo Shell). */
export function NaoEncontrada({ aoBuscar }: { aoBuscar: () => void }) {
  return (
    <div className="es-pagina">
      <div className="es-404">404</div>
      <div className="es-vazio-titulo">Não encontramos esta página</div>
      <div className="es-vazio-texto">
        O endereço pode ter mudado. Tente pela busca ou volte para o início.
      </div>
      <div className="es-acoes">
        <button type="button" className="es-botao" onClick={aoBuscar}>
          <Icone nome="search" tamanho={15} traco={1.6} />
          Buscar
        </button>
        <a className="es-botao es-botao-primario" href="#/visao-geral">
          Ir para o início
        </a>
      </div>
    </div>
  );
}

/**
 * A FAIXA DE CONEXÃO — 36px, no topo do conteúdo, nunca um modal por cima da venda.
 * Sem conexão ela avisa; quando a internet volta ela diz "restabelecida" por uns segundos e sai.
 */
export function FaixaDeConexao() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [voltou, setVoltou] = useState(false);

  useEffect(() => {
    let timer: number | undefined;
    const caiu = () => {
      setOnline(false);
      setVoltou(false);
    };
    const subiu = () => {
      setOnline(true);
      setVoltou(true);
      timer = window.setTimeout(() => setVoltou(false), 4000);
    };
    window.addEventListener('offline', caiu);
    window.addEventListener('online', subiu);
    return () => {
      window.removeEventListener('offline', caiu);
      window.removeEventListener('online', subiu);
      window.clearTimeout(timer);
    };
  }, []);

  if (!online) {
    return (
      <div className="es-faixa es-faixa-off" role="status" aria-live="polite">
        <Icone nome="wifi" tamanho={16} traco={1.6} />
        <span>Você está sem conexão. Algumas ações ficam indisponíveis.</span>
        <button type="button" className="es-faixa-acao" onClick={() => window.location.reload()}>
          Tentar agora
        </button>
      </div>
    );
  }
  if (voltou) {
    return (
      <div className="es-faixa es-faixa-ok" role="status" aria-live="polite">
        <Icone nome="check" tamanho={16} traco={1.8} />
        <span>Conexão restabelecida.</span>
      </div>
    );
  }
  return null;
}

/** Toast de confirmação rápida (grafite, canto inferior). Some sozinho. */
export type Aviso = { readonly id: number; readonly texto: string; readonly icone?: NomeDoIcone };

export function Avisos({ avisos }: { avisos: readonly Aviso[] }) {
  return (
    <div className="es-toasts" aria-live="polite">
      {avisos.map((a) => (
        <div key={a.id} className="es-toast" role="status">
          <Icone nome={a.icone ?? 'check'} tamanho={16} traco={1.8} />
          <span>{a.texto}</span>
        </div>
      ))}
    </div>
  );
}
